import { css, Button, Text, tokens } from "@balabalabom/ui";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { miniBookService, getBaseUrl } from "../lib/miniBookService";

const ORANGE = "#e89b5f";
const CARD_BG = "#f8f6f2";
const BORDER = "#e8e4dc";

export function MiniBookInstructionBackground() {
  const navigate = useNavigate();
  const [backgrounds, setBackgrounds] = useState<{ id: number; title: string; url: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBackgrounds = async () => {
      try {
        setLoading(true);
        setError(null);
        const list = await miniBookService.listInstructionBackgrounds(true, false);
        setBackgrounds(list.map((b) => ({ id: b.id, title: b.title, url: b.url })));
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to load instruction backgrounds";
        setError(msg);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchBackgrounds();
  }, []);

  const handleSelect = (bg: { id: number; title: string; url: string }) => {
    navigate("/minibook", {
      state: { selectedInstructionBackground: bg },
    });
  };

  const imgSrc = (url: string) =>
    url.startsWith("http") ? url : `${getBaseUrl()}${url}`;

  if (loading) {
    return (
      <main className="main-mobile" style={css({ maxWidth: "80rem", margin: "0 auto", textAlign: "center", padding: "2rem" })}>
        <Text variant="body" style={css({ color: ORANGE })}>
          Loading instruction backgrounds...
        </Text>
      </main>
    );
  }

  if (error) {
    return (
      <main className="main-mobile" style={css({ maxWidth: "80rem", margin: "0 auto", textAlign: "center", padding: "2rem" })}>
        <Text variant="body" style={css({ color: "#ef4444", marginBottom: "1rem" })}>
          {error}
        </Text>
        <Button variant="primary" onClick={() => window.location.reload()} style={css({ backgroundColor: ORANGE })}>
          Retry
        </Button>
      </main>
    );
  }

  return (
    <main
      className="main-mobile"
      style={css({
        maxWidth: "80rem",
        margin: "0 auto",
        padding: "1.5rem",
        minHeight: "60vh",
        backgroundImage: `radial-gradient(circle at 20% 30%, rgba(253, 224, 71, 0.12) 0%, transparent 50%),
          radial-gradient(circle at 80% 70%, rgba(253, 224, 71, 0.08) 0%, transparent 40%)`,
      })}
    >
      <div
        style={css({
          backgroundColor: CARD_BG,
          borderRadius: "1.25rem",
          border: `1px solid ${BORDER}`,
          padding: "2rem",
          boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        })}
      >
        <h2
          style={css({
            fontSize: "1.5rem",
            fontWeight: 700,
            color: ORANGE,
            margin: 0,
            marginBottom: "0.5rem",
            textAlign: "center",
          })}
        >
          Select background paper
        </h2>
        <Text
          variant="body"
          style={css({ color: tokens.colors.neutral[600], marginBottom: "1.5rem", textAlign: "center" })}
        >
          Choose an instruction background for your mini-book.
        </Text>
        <div
          style={css({
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: "1.5rem",
          })}
        >
          {backgrounds.map((bg) => (
            <button
              key={bg.id}
              type="button"
              onClick={() => handleSelect(bg)}
              style={css({
                cursor: "pointer",
                border: `2px solid ${BORDER}`,
                borderRadius: "0.75rem",
                overflow: "hidden",
                padding: 0,
                background: "white",
                transition: "transform 0.2s, box-shadow 0.2s, border-color 0.2s",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                textAlign: "left",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  borderColor: ORANGE,
                },
              })}
            >
              <div
                style={css({
                  aspectRatio: "1",
                  minHeight: 120,
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f5f0e8",
                })}
              >
                <img
                  src={imgSrc(bg.url)}
                  alt={bg.title}
                  style={css({
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  })}
                />
              </div>
              <div style={css({ padding: "0.75rem 1rem" })}>
                <Text
                  variant="body"
                  style={css({
                    fontWeight: 600,
                    color: tokens.colors.neutral[800],
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  })}
                >
                  {bg.title}
                </Text>
                <Text variant="body" style={css({ color: tokens.colors.neutral[600], marginTop: "0.25rem", fontSize: "0.875rem" })}>
                  Select
                </Text>
              </div>
            </button>
          ))}
        </div>
        {backgrounds.length === 0 && (
          <Text variant="body" style={css({ color: tokens.colors.neutral[600], textAlign: "center", padding: "2rem" })}>
            No instruction backgrounds available yet.
          </Text>
        )}
        <div style={css({ marginTop: "1.5rem", textAlign: "center" })}>
          <Button
            variant="secondary"
            onClick={() => navigate("/minibook")}
            style={css({
              borderColor: BORDER,
              color: tokens.colors.neutral[700],
            })}
          >
            ← Back to create
          </Button>
        </div>
      </div>
    </main>
  );
}
